/** A horizontal bar chart with an accessibility table behind it.
 *
 * Never a table as the primary view (`30-design-principles.md`), but always a table available:
 * a bar communicates proportion at a glance and communicates nothing to a screen reader.
 *
 * Each row is clickable when it has provenance to show, which is how a chart participates in the
 * receipt rule rather than being a dead-end picture of numbers.
 */

import { useId, useState } from "react";

import styles from "./Bars.module.css";

export interface BarRow {
  id: string;
  name: string;
  /** Display text — already formatted and rounded by `lib/format`. */
  value: string;
  /** 0 to 1. Computed by the caller, which knows the denominator. */
  fraction: number;
  colour: string;
  meta?: string;
  onOpen?: () => void;
}

interface Props {
  rows: readonly BarRow[];
  /** What the table's caption says the numbers are, e.g. "tCO₂e by facility". */
  caption: string;
  valueHeader?: string;
}

export function Bars({ rows, caption, valueHeader = "tCO₂e" }: Props) {
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  return (
    <div className={styles.wrap}>
      {showTable ? (
        <table className={styles.table} id={tableId}>
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">{valueHeader}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  {row.name}
                  {row.meta ? <span className={styles.meta}>{row.meta}</span> : null}
                </th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={styles.rows}>
          {rows.map((row) => {
            const Element = row.onOpen ? "button" : "div";
            return (
              <Element
                key={row.id}
                {...(row.onOpen
                  ? {
                      type: "button" as const,
                      onClick: row.onOpen,
                      "aria-label": `${row.name}: ${row.value} ${valueHeader}. Show where this came from`,
                    }
                  : {})}
                className={`${styles.row} ${row.onOpen ? "" : styles.rowStatic}`}
              >
                <span className={styles.name}>{row.name}</span>
                <span className={styles.track}>
                  <span
                    className={styles.fill}
                    style={{
                      width: `${Math.max(0, Math.min(100, row.fraction * 100))}%`,
                      background: row.colour,
                    }}
                  />
                </span>
                <span className={styles.value}>
                  {row.value}
                  {row.meta ? <span className={styles.meta}>{row.meta}</span> : null}
                </span>
              </Element>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className={styles.toggle}
        onClick={() => setShowTable((shown) => !shown)}
        aria-expanded={showTable}
        aria-controls={showTable ? tableId : undefined}
      >
        {showTable ? "Show chart" : "Show as table"}
      </button>
    </div>
  );
}
